import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, toDropdownFieldMeta } from "./formFieldHelpers";
import DropdownField, { DropdownOption } from "../../../common/formFields/DropdownField";
import { generatePickerOptions } from "../../../common/utils/appUtils";

/**
 * Shared "Audit Type" field (Spec #35289, AC1). Ports the Classic Audit(53)
 * control (DataEntryDetailsRightPane.tsx:2107-2120) into Quick Entry as a plain
 * DropdownField, mirroring ClinicResponsibleField. Options come from the same
 * redux slice Classic reads - state.dataEntryState.auditTypes ({ titleText,
 * auditTypeID }), fetched once by getAuditTypesAPI (proc ROIS_RequestorAuditType_sel).
 *
 * Manual-entry only (Assumption A1): the value is held in DATA_ENTRY_FIELDS_FORM
 * and NOT persisted until Confirm All - this component only writes to the form.
 */
/**
 * The shape state.dataEntryState.auditTypes holds - the same object Classic feeds to
 * pickerHelperFunction(props.auditTypes, "titleText", "auditTypeID"). Rows may carry
 * extra server fields; only the two this component reads are declared.
 */
export interface AuditTypeOption {
    readonly titleText: string;
    readonly auditTypeID: number;
}

export interface AuditTypeFieldProps {
    /** Currently selected audit-type id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** state.dataEntryState.auditTypes - option source, built the same way Classic does. */
    readonly auditTypes?: readonly AuditTypeOption[];
    readonly dispatch: Function;
    readonly formName?: string;
    /** Non-form side effect the caller runs on change (Quick Entry marks the row as edited). */
    readonly onAuditTypeChange?: (newValue: number) => void;
    /** Real redux-form validation state, threaded down by Classic; omitted by Quick Entry. */
    readonly meta?: ClassicFieldMeta;
}

const AuditTypeField: React.FC<AuditTypeFieldProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const options: readonly DropdownOption[] = generatePickerOptions(props.auditTypes, "titleText", "auditTypeID")
        .map(option => ({ label: option.label, value: option.value }));

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.AUDIT_TYPE, newValue, formName);
        props.onAuditTypeChange?.(newValue);
    };

    return (
        <DropdownField
            label="Audit Type"
            required
            meta={toDropdownFieldMeta(props.meta)}
            options={options}
            onChange={onChange}
            value={props.value}
        />
    );
};

export default AuditTypeField;

export const AUDIT_TYPE_ROW_ID = "auditType";

/**
 * Builds the FieldGroupRow for the Audit Type row. Always required when visible
 * (the row is only rendered for Audit(53)); a real audit-type id is a positive
 * database id, so "- Select -" (DEFAULT_DROPDOWN_VALUE, -9999) and 0/""/blank all
 * read NeedsEntry - which is what blocks Confirm All (AC3) via the same
 * outstanding-row gate ClinicResponsible uses.
 */
export function mapAuditTypeToFieldGroupRow(badgeNumber: number, formValue: number | undefined, auditTypes?: readonly AuditTypeOption[]): FieldGroupRow {
    const numericValue = Number(formValue);
    const hasValue = formValue !== undefined && formValue !== null && (formValue as any) !== ""
        && Number.isFinite(numericValue) && numericValue > 0;
    const list: readonly any[] = Array.isArray(auditTypes)
        ? auditTypes
        : auditTypes ? Object.values(auditTypes) : [];
    const matched = hasValue
        ? list.find((at: any) => Number(at?.auditTypeID) === numericValue)
        : undefined;

    return {
        id: AUDIT_TYPE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Audit Type",
        value: matched?.titleText ?? (hasValue ? String(formValue) : null),
        isAiPreSelected: false,
    };
}
